/**
 * Language and writing direction.
 *
 * Arabic is the default, not a translation of an English original (DEC-008): the people using
 * this dashboard are Egyptian clinic staff. English is offered because some clinic owners and
 * most developers prefer it.
 *
 * Direction is applied to the document element rather than to individual components, so a single
 * attribute mirrors the whole interface — layout, borders, padding, icon placement — through CSS
 * logical properties. There is no second stylesheet to keep in sync (NFR-006).
 */

export const SUPPORTED_LOCALES = ['ar', 'en'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'ar';

export type TextDirection = 'rtl' | 'ltr';

const RIGHT_TO_LEFT_LANGUAGES: ReadonlySet<string> = new Set(['ar', 'he', 'fa', 'ur']);

/** The storage key the language choice persists under, shared with the API client's header. */
export const LOCALE_STORAGE_KEY = 'laseera.locale';

export function isSupportedLocale(candidate: unknown): candidate is SupportedLocale {
  return typeof candidate === 'string' && SUPPORTED_LOCALES.includes(candidate as SupportedLocale);
}

/**
 * Maps anything a browser might report — `ar-EG`, `en-GB`, `fr` — onto a locale we actually
 * ship, falling back to Arabic rather than to English.
 */
export function normaliseLocale(candidate: string | null | undefined): SupportedLocale {
  if (!candidate) {
    return DEFAULT_LOCALE;
  }
  const language = candidate.toLowerCase().split('-')[0] ?? '';
  return isSupportedLocale(language) ? language : DEFAULT_LOCALE;
}

export function directionOf(locale: string | null | undefined): TextDirection {
  const language = (locale ?? '').toLowerCase().split('-')[0] ?? '';
  return RIGHT_TO_LEFT_LANGUAGES.has(language) ? 'rtl' : 'ltr';
}

/**
 * Applies a locale to the document: `lang` for screen readers and hyphenation, `dir` for layout.
 *
 * Kept as a plain function taking the document so it can be tested without a component tree.
 */
export function applyDocumentLocale(locale: string, doc: Document = document): void {
  const normalised = normaliseLocale(locale);
  doc.documentElement.lang = normalised;
  doc.documentElement.dir = directionOf(normalised);
}
